package no.vareflow.web

import no.vareflow.app.core.VareflowCore
import no.vareflow.app.domain.*

/**
 * The old "Lokal test" (test data only in this browser) is for development only now:
 * it appears just with `?test` in the address. Everyone else always logs in to the cloud.
 */
val webTestMode: Boolean by lazy {
    window.location.search.unsafeCast<String>().let { Regex("[?&]test(=|&|$)").containsMatchIn(it) }
}

private val loginState = object {
    var username = ""
    var password = ""
    var remember = false
    var busy = false
    /** "cloud" (Firebase) or "local" (test data in this browser). */
    var mode = "cloud"
    /** Firebase: "email" or "code" (Firmakode + brukernavn, for people without e-mail). */
    var cloudBy = ""
    var companyCode = ""
    var codeInfo: CompanyCodeInfo? = null
    var codeChecked = ""
    var codeFailed = false
    var codeLookupStarted = false
    var choices: List<CompanyChoice>? = null
    /** Signed in with a start code: must choose an own password before the app opens. */
    var mustChoose: WebMembership? = null
    var startCode = ""
    var newPassword = ""
    var newPassword2 = ""
    var passwordError: String? = null
    /** "Glemt passord?" panel. */
    var forgotOpen = false
    var resetEmail = ""
    var resetBusy = false
    /** The address the reset link went to (shows the "sjekk e-posten" message). */
    var resetSentTo: String? = null
    var resetError: String? = null
    /** The page was opened from a helper's link/QR code (?firma=…&bruker=…). */
    var fromLink = false
    var linkChecked = false
}

fun WebApp.renderLogin(root: El) {
    val s = loginState
    if (!s.linkChecked) {
        s.linkChecked = true
        // Helper link / QR code: vareflow.no/?firma=SP99&bruker=ola fills in the Firmakode login.
        val link = runCatching { HelperAccess.parseLink(window.location.search as String) }.getOrNull()
        if (link != null) {
            s.cloudBy = "code"
            s.companyCode = link.first
            if (link.second.isNotBlank()) s.username = link.second
            s.fromLink = true
            s.codeLookupStarted = false
            // Keep the address clean, so a reload or a bookmark does not carry the brukernavn.
            runCatching { window.history.replaceState(null, "", window.location.pathname) }
        }
    }
    if (s.cloudBy.isEmpty()) {
        s.companyCode = cloud.lastCompanyCode()
        s.cloudBy = if (s.companyCode.isNotBlank()) "code" else "email"
    }
    if (!webTestMode) s.mode = "cloud" else if (!cloud.available) s.mode = "local"
    val inCloud = s.mode == "cloud"
    root.div("login-page") {
        div("login-hero") {
            div("login-brand", "Vareflow")
            p(t("Rask og kontrollert varetelling", "Fast and controlled stock counting"), "login-tagline")
            div("lang-switch") {
                attr("role", "group")
                attr("aria-label", t("Språk", "Language"))
                AppLanguage.entries.forEach { language ->
                    el("button", "lang-option" + if (language == I18n.language) " active" else "", language.nativeName) {
                        attr("type", "button")
                        attr("lang", language.htmlLang)
                        attr("aria-pressed", (language == I18n.language).toString())
                        onClick { if (language != I18n.language) { loginError = null; setLanguage(language, saveToAccount = false) } }
                    }
                }
            }
        }
        s.mustChoose?.let { m -> return@div renderChoosePassword(this, m) }
        s.choices?.let { list -> return@div renderCompanyPicker(this, list) }
        if (s.forgotOpen && inCloud) return@div renderForgotPassword(this)
        el("form", "card login-card") {
            attr("autocomplete", "on")
            on("submit") { e ->
                e.preventDefault()
                if (!s.busy) submitLogin()
            }
            h1(t("Logg inn", "Log in"), "login-title")
            if (restoringCloud) p(t("Kobler til …", "Connecting …"), "muted small")
            if (webTestMode) div("chip-row wrap") {
                chip(t("Sky", "Cloud"), inCloud) { s.mode = "cloud"; loginError = null; requestRender() }
                chip(t("Lokal test", "Local test"), !inCloud) { s.mode = "local"; loginError = null; requestRender() }
            }
            if (!cloud.available) {
                p(t("Kunne ikke koble til Vareflow. Sjekk nettforbindelsen og last siden på nytt.", "Could not connect to Vareflow. Check the network connection and reload the page."), "warning-text")
                if (!webTestMode) button(t("Last inn på nytt", "Reload"), "btn outline block") { window.location.reload() }
            }
            if (inCloud) {
                div("chip-row wrap") {
                    chip(t("E-post", "E-mail"), s.cloudBy == "email") { s.cloudBy = "email"; loginError = null; requestRender() }
                    chip(t("Firmakode", "Company code"), s.cloudBy == "code") { s.cloudBy = "code"; loginError = null; requestRender() }
                }
                if (s.cloudBy == "code") {
                    // A remembered Firmakode: look it up once so the company name shows right away.
                    if (s.codeInfo == null && s.codeChecked.isEmpty() && !s.codeFailed && !s.codeLookupStarted && CompanyCode.isValid(CompanyCode.normalize(s.companyCode))) {
                        s.codeLookupStarted = true
                        checkCompanyCode()
                    }
                    field(t("Firmakode", "Company code"), s.companyCode, "login-code", placeholder = t("f.eks. SP99", "e.g. SP99"), autocomplete = "organization") {
                        s.companyCode = it.uppercase().take(8)
                        checkCompanyCode()
                    }
                    when {
                        s.codeInfo != null -> div("ok-text strong", t("Logg inn hos ${s.codeInfo!!.name}", "Log in at ${s.codeInfo!!.name}"))
                        s.codeFailed -> div("error-text", t("Kunne ikke sjekke Firmakoden. Er du på nett?", "Could not check the company code. Are you online?"))
                        s.codeChecked.isNotEmpty() -> div("error-text", t("Ukjent Firmakode. Spør lederen din.", "Unknown company code. Ask your leader."))
                    }
                }
            } else {
                p(t("Testmodus: data lagres bare i denne nettleseren.", "Test mode: data is stored only in this browser."), "muted small")
            }
            val userLabel = if (inCloud && s.cloudBy == "email") t("E-post", "E-mail") else t("Brukernavn", "Username")
            field(userLabel, s.username, "login-user", type = if (inCloud && s.cloudBy == "email") "email" else "text", autocomplete = "username") { s.username = it }
            field(if (s.fromLink && inCloud && s.cloudBy == "code") t("Startkode / passord", "Start code / password") else t("Passord", "Password"),
                s.password, "login-pass", type = "password", autocomplete = "current-password",
                hint = if (s.fromLink && inCloud && s.cloudBy == "code") t("Koden du fikk av lederen din, f.eks. K7MP-4XQ2.", "The code you got from your leader, e.g. K7MP-4XQ2.") else null) { s.password = it }
            if (inCloud) {
                el("button", "btn text small forgot-link", t("Glemt passord?", "Forgot password?")) {
                    attr("type", "button")
                    onClick { openForgotPassword() }
                }
            }
            el("label", "check remember") {
                el("input") {
                    attr("type", "checkbox")
                    attr("data-key", "login-remember")
                    node.checked = s.remember
                    on("change") { s.remember = node.checked as Boolean }
                }
                div {
                    div("remember-title", t("Husk meg i denne nettleseren", "Remember me in this browser"))
                    div("muted tiny", t("Passordet lagres ikke som lesbar tekst.", "The password is not stored as readable text."))
                }
            }
            errorText(loginError)
            core.loadWarnings.forEach { div("warning-text", it) }
            val canSubmit = !s.busy && !restoringCloud && (!inCloud || (cloud.available && (s.cloudBy != "code" || s.codeInfo != null)))
            el("button", "btn primary block", if (s.busy) t("Kobler til …", "Connecting …") else t("Logg inn", "Log in")) {
                attr("type", "submit")
                node.disabled = !canSubmit
            }
            if (inCloud && s.cloudBy == "email") {
                div("muted small center", t("eller", "or"))
                button(t("Fortsett med Google", "Continue with Google"), "btn outline block", enabled = !s.busy && !restoringCloud) {
                    s.busy = true
                    loginError = null
                    requestRender()
                    cloud.signInWithGoogle { result -> handleSignIn(result) }
                }
            }
        }
    }
}

private fun WebApp.submitLogin() {
    val s = loginState
    if (s.mode == "local") {
        when (val result = core.login(s.username, s.password)) {
            is VareflowCore.LoginResult.Success -> {
                s.password = ""
                login(result.user, s.remember)
            }
            is VareflowCore.LoginResult.Failure -> { loginError = result.message; requestRender() }
        }
        return
    }
    s.busy = true
    loginError = null
    requestRender()
    val typed = s.password
    if (s.cloudBy == "code") {
        cloud.signInWithCompanyCode(s.companyCode, s.username, typed) { result ->
            s.busy = false
            result.onSuccess { finishCloudLogin(it, typed) }.onFailure { loginError = it.message; requestRender() }
        }
    } else {
        cloud.signIn(s.username, typed) { result -> handleSignIn(result, typed) }
    }
}

private fun WebApp.handleSignIn(result: Result<WebSignIn>, typedPassword: String = "") {
    val s = loginState
    s.busy = false
    result.onSuccess { outcome ->
        when (outcome) {
            is WebSignIn.Ready -> finishCloudLogin(outcome.membership, typedPassword)
            is WebSignIn.Choose -> { s.choices = outcome.choices; s.startCode = typedPassword; requestRender() }
        }
    }.onFailure { e ->
        if (e !is LoginCancelled) loginError = e.message
        requestRender()
    }
}

/** End of every Firebase login: an own password first when needed, then into the app. */
private fun WebApp.finishCloudLogin(membership: WebMembership, typedPassword: String) {
    val s = loginState
    s.password = ""
    s.choices = null
    if (membership.mustChangePassword) {
        s.mustChoose = membership
        s.startCode = typedPassword
        s.newPassword = ""
        s.newPassword2 = ""
        s.passwordError = null
        requestRender()
        return
    }
    openOwnerHomeOnce = true
    cloud.pendingLoginVisit = true
    activateCloud(membership, s.remember)
}

private fun WebApp.checkCompanyCode() {
    val s = loginState
    val code = CompanyCode.normalize(s.companyCode)
    s.codeInfo = null
    s.codeFailed = false
    s.codeChecked = ""
    if (!CompanyCode.isValid(code)) return
    cloud.lookupCompanyCode(code) { result ->
        if (CompanyCode.normalize(s.companyCode) != code) return@lookupCompanyCode
        s.codeInfo = result.getOrNull()
        s.codeFailed = result.isFailure
        s.codeChecked = if (result.isSuccess) code else ""
        requestRender()
    }
}

private fun WebApp.renderCompanyPicker(root: El, choices: List<CompanyChoice>) {
    val s = loginState
    root.div("card login-card") {
        h1(t("Velg bedrift", "Choose company"), "login-title")
        p(t("Du er med i flere bedrifter. Hvilken vil du åpne nå?", "You belong to several companies. Which one do you want to open now?"), "muted small")
        div("list") {
            choices.forEach { choice ->
                el("button", "card nav-card company-choice") {
                    attr("type", "button")
                    div("company-monogram", CompanyCode.monogram(choice.name))
                    div("grow") {
                        div("card-title", choice.name)
                        div("muted small", when (choice.role.uppercase()) { "ADMIN" -> "Admin"; "LEADER" -> t("Leder", "Leader"); else -> t("Ansatt", "Employee") })
                    }
                    span("chevron", "›").attr("aria-hidden", "true")
                    onClick {
                        s.busy = true
                        requestRender()
                        cloud.openCompany(choice.companyId) { result ->
                            s.busy = false
                            result.onSuccess { finishCloudLogin(it, s.startCode) }
                                .onFailure { s.choices = null; loginError = it.message; requestRender() }
                        }
                    }
                }
            }
        }
        button(t("Avbryt", "Cancel"), "btn text block") {
            s.choices = null
            cloud.signOut()
            requestRender()
        }
    }
}

private fun WebApp.renderChoosePassword(root: El, membership: WebMembership) {
    val s = loginState
    root.el("form", "card login-card") {
        on("submit") { e ->
            e.preventDefault()
            s.passwordError = CompanyLogin.newPasswordProblem(s.newPassword, s.newPassword2, s.startCode)
            if (s.passwordError != null) { requestRender(); return@on }
            s.busy = true
            requestRender()
            cloud.changeOwnPassword(s.newPassword) { result ->
                s.busy = false
                result.onSuccess {
                    s.mustChoose = null
                    s.startCode = ""
                    openOwnerHomeOnce = true
                    cloud.pendingLoginVisit = true
                    activateCloud(membership.copy(mustChangePassword = false), s.remember)
                }.onFailure { s.passwordError = it.message; requestRender() }
            }
        }
        h1(t("Velg ditt eget passord", "Choose your own password"), "login-title")
        p(t("Startkoden fra lederen din gjelder bare første gang. Velg et passord som bare du kjenner (minst ${CompanyLogin.MIN_PASSWORD} tegn).", "The start code from your leader only works the first time. Choose a password only you know (at least ${CompanyLogin.MIN_PASSWORD} characters)."), "muted small")
        field(t("Nytt passord", "New password"), s.newPassword, "np-1", type = "password", autocomplete = "new-password") { s.newPassword = it }
        field(t("Gjenta passordet", "Repeat the password"), s.newPassword2, "np-2", type = "password", autocomplete = "new-password") { s.newPassword2 = it }
        errorText(s.passwordError)
        el("button", "btn primary block", if (s.busy) t("Lagrer …", "Saving …") else t("Lagre og fortsett", "Save and continue")) {
            attr("type", "submit")
            node.disabled = s.busy
        }
        button(t("Logg ut", "Log out"), "btn text block") {
            s.mustChoose = null
            cloud.signOut()
            requestRender()
        }
    }
}

private fun WebApp.openForgotPassword() {
    val s = loginState
    s.forgotOpen = true
    s.resetEmail = if (s.cloudBy == "email") s.username.trim() else ""
    s.resetSentTo = null
    s.resetError = null
    s.resetBusy = false
    loginError = null
    requestRender()
}

/** A plain check before asking Firebase; the internal Firmakode addresses never get e-mail. */
internal fun resetEmailProblem(email: String): String? {
    val e = email.trim().lowercase()
    val at = e.indexOf('@')
    return when {
        e.isEmpty() -> t("Skriv inn e-postadressen du logger inn med.", "Enter the e-mail address you log in with.")
        at <= 0 || e.indexOf('.', at) < 0 || e.endsWith(".") || e.contains(' ') -> t("Skriv inn en gyldig e-postadresse.", "Enter a valid e-mail address.")
        e.endsWith(".users.vareflow.no") -> t("Denne kontoen har ikke e-post. Spør lederen din om hjelp.", "This account has no e-mail. Ask your leader for help.")
        else -> null
    }
}

/**
 * "Glemt passord?": people with e-mail get Firebase's reset link; people who log in with
 * Firmakode have no e-mail, so they are told to ask their leader.
 */
private fun WebApp.renderForgotPassword(root: El) {
    val s = loginState
    val close = {
        s.forgotOpen = false
        s.resetSentTo = null
        s.resetError = null
        requestRender()
    }
    if (s.cloudBy == "code") {
        root.div("card login-card") {
            h1(t("Glemt passord?", "Forgot password?"), "login-title")
            p(t("Du logger inn med Firmakode og brukernavn, uten e-post. Da kan ikke passordet tilbakestilles her.", "You log in with company code and username, without e-mail. Then the password cannot be reset here."), "muted small")
            p(t("Spør lederen eller admin i bedriften din om hjelp. Har du e-post på kontoen, velg «E-post» og prøv igjen.", "Ask the leader or an admin in your company for help. If your account has an e-mail, choose «E-mail» and try again."), "muted small")
            button(t("Logg inn med e-post", "Log in with e-mail"), "btn outline block") {
                s.cloudBy = "email"
                openForgotPassword()
            }
            button(t("Tilbake til innlogging", "Back to login"), "btn text block") { close() }
        }
        return
    }
    val sentTo = s.resetSentTo
    if (sentTo != null) {
        root.div("card login-card") {
            h1(t("Sjekk e-posten", "Check your e-mail"), "login-title")
            p(t("Hvis det finnes en Vareflow-konto for $sentTo, har vi sendt en lenke for å velge nytt passord.", "If there is a Vareflow account for $sentTo, we have sent a link to choose a new password."), "small")
            p(t("Finner du den ikke? Se i søppelpost/spam. Lenken virker en begrenset tid.", "Can't find it? Look in junk/spam. The link works for a limited time."), "muted small")
            button(t("Tilbake til innlogging", "Back to login"), "btn primary block") {
                if (s.username.isBlank()) s.username = sentTo
                close()
            }
            button(t("Send på nytt", "Send again"), "btn text block", enabled = !s.resetBusy) {
                s.resetSentTo = null
                requestRender()
            }
        }
        return
    }
    root.el("form", "card login-card") {
        on("submit") { e ->
            e.preventDefault()
            if (s.resetBusy) return@on
            s.resetError = resetEmailProblem(s.resetEmail)
            if (s.resetError != null) { requestRender(); return@on }
            val email = s.resetEmail.trim()
            s.resetBusy = true
            requestRender()
            cloud.sendPasswordReset(email) { result ->
                s.resetBusy = false
                result.onSuccess { s.resetSentTo = email; s.resetError = null }
                    .onFailure { s.resetError = it.message ?: t("Kunne ikke sende e-posten.", "Could not send the e-mail.") }
                requestRender()
            }
        }
        h1(t("Glemt passord?", "Forgot password?"), "login-title")
        p(t("Skriv e-postadressen du logger inn med. Du får en lenke for å velge nytt passord.", "Enter the e-mail address you log in with. You will get a link to choose a new password."), "muted small")
        field(t("E-post", "E-mail"), s.resetEmail, "reset-email", type = "email", autocomplete = "username") { s.resetEmail = it; s.resetError = null }
        errorText(s.resetError)
        el("button", "btn primary block", if (s.resetBusy) t("Sender …", "Sending …") else t("Send lenke", "Send link")) {
            attr("type", "submit")
            node.disabled = s.resetBusy
        }
        button(t("Tilbake til innlogging", "Back to login"), "btn text block") { close() }
    }
}
